import type { DecryptStatus } from "@/types/message";

export const MAX_FILE_SIZE_IN_KB_STRING = `1 MB`;

export const DELETE_CONVERSATION_HEADER = `This action cannot be undone. This conversation will be deleted and the messages will be removed from our servers.`;
export const DELETE_CONVERSATION_SUBHEADER = `If you are the owner of this group, deleting this conversation will delete the group.`;
export const DELETE_CONVERSATION_TITLE = `Delete this conversation?`;
// Group wording follows what the backend actually does: an owner deletes the whole group
// (DBAdminDeleteGroup), a member is only removed from it (DBAdminDeleteGroupMembers). The
// generic pair above covers the case where ownership can't be resolved.
export const DELETE_GROUP_OWNER_TITLE = `Delete this group?`;
export const DELETE_GROUP_OWNER_HEADER = `You own this group. Deleting it removes the group for everyone — all members lose the conversation and its messages.`;
export const DELETE_GROUP_OWNER_SUBHEADER = `This action cannot be undone.`;
export const DELETE_GROUP_OWNER_ACTION = `Delete for everyone`;
export const LEAVE_GROUP_TITLE = `Leave this group?`;
export const LEAVE_GROUP_DESCRIPTION = `You'll be removed from this group and your copy of its messages deleted. The group stays active for everyone else.`;
export const LEAVE_GROUP_ACTION = `Leave group`;
export const DELETE_CONVERSATION_DESCRIPTION = `This action cannot be undone. This conversation will be deleted and the messages will 
be removed from our servers.`;
export const DELETE_CONVERSATION_CANCEL = `Cancel`;
export const DELETE_CONVERSATION_ACTION = `Continue`;

export const SEARCH_CONVERSATION_TITLE = `Search`;
export const SEARCH_CONVERSATION_PLACEHOLDER = `Search in conversation`;
export const SEARCH_CONVERSATION_DESCRIPTION = `Press "Enter" to search.`;
export const NO_RESULTS_TITLE = `No results found`;
export const NO_RESULTS_DESCRIPTION = `Try again with a different spelling or combination of terms.`;

export const NEW_CONVERSATION_TITLE = `New Message`;
// REVISIT: these two enumerate the destination kinds, and that list keeps growing — usernames,
// then user@router-alias, then email. Catalina ("#transponder") is accepted but left out rather
// than extending the sentence a fourth time. Rework into something that doesn't scale with the
// gateway list. Paired with the note on isCatalinaAddress in utils/addresses.ts.
export const NEW_CONVERSATION_DESCRIPTION = `Enter a contact's username, user@router-alias for another router, or an email address.`;
export const NEW_CONVERSATION_PLACEHOLDER = `Username, user@router-alias, or email...`;
export const NEW_CONVERSATION_ADDRESS_NOT_FOUND = `No users or groups found with that name.`;
export const NEW_CONVERSATION_ACTION = `Start conversation`;
export const NEW_CONVERSATION_SUGGESTION_PLACEHOLDER = `Fetching suggestions. Please wait...`;
// Offered when the typed text isn't a local user but is a routable external address.
export const NEW_CONVERSATION_FEDERATED_SUGGESTION = (address: string) =>
  `${address} (another router)`;
export const NEW_CONVERSATION_EMAIL_SUGGESTION = (address: string) =>
  `${address} (email)`;
export const NEW_CONVERSATION_CATALINA_SUGGESTION = (address: string) =>
  `${address} (Catalina)`;

// Shown above the composer in an email conversation: the recipient's reply goes to the router's
// configured reply-to, so the only messages arriving here are its own delivery notices.
export const EMAIL_CONVERSATION_NOTICE = `Sent as email — replies won't appear in this conversation.`;

export const CONTROL_LOAD_ERROR_MSG = `Error loading conversations`;
export const CONTROL_SEARCH_PLACEHOLDER = `Search for an existing conversation...`;

// Shown in place of an encrypted message that couldn't be decrypted (MessageType.decryptStatus).
export const DECRYPT_STATUS_TEXT: Record<DecryptStatus, string> = {
  newKey: `[Encrypted with new key]`,
  oldKey: `[Encrypted with old key]`,
  error: `[Something went wrong decrypting this message]`,
};

// Message status icon tooltips (components/Chat/Messages/IconWithTooltip.tsx).
export const MESSAGE_ENCRYPTED_TOOLTIP = `End-to-end encrypted`;
export const MESSAGE_SATELLITE_TOOLTIP = `Sent via satellite`;
export const MESSAGE_UNVERIFIED_SENDER_TOOLTIP = `Sender not verified`;

// Manual key sync button next to the encryption shield (components/Controls/KeySyncButton.tsx).
export const KEY_SYNC_GROUP_TOOLTIP = `Manually fetch owner's key`;
export const KEY_SYNC_CONTACT_TOOLTIP = `Manually fetch contact's key`;
export const KEY_SYNC_OWNER_TOOLTIP = `Sync member key requests`;

export const CHAT_WELCOME_TITLE = `Welcome to SpaceLinq for Web`;
export const CHAT_WELCOME_DESCRIPTION = `Press to create a conversation`;
export const CHAT_NO_CONVERSATION_SELECTED = `Select a conversation to begin sending messages`;
export const CHAT_EMPTY_CONVERSATION = `Send a message to start your conversation`;

export const CHAT_FILE_UPLOAD_DRAGDROP_TITLE = `Upload File`;
export const CHAT_FILE_UPLOAD_DRAGDROP_DESCRIPTION = `Select a file to send with your message.`;
export const CHAT_FILE_UPLOAD_DRAGDROP_ACCEPTED_FILES_AND_SIZE = `Accepted files: JPEG, PNG, TXT, PDF, JSON, binary, and office documents (.docx, .xlsx) under 1 MB.`;
export const CHAT_FILE_UPLOAD_DRAGDROP_MESSAGE = `Drag and drop to upload file or click to browse`;
export const CHAT_FILE_UPLOAD_DRAGDROP_IMAGE_CONVERSION_INFO = `Converting/compressing image for satellite transfer. Please wait!`;
export const CHAT_FILE_UPLOAD_DRAGDROP_IMAGE_CONVERSION_WARNING = `This image will be converted to WEBP.`;
export const CHAT_FILE_UPLOAD_SIZE_WARNING = `Warning! Your file exceeds 12 KB. Large files will take SIGNIFICANTLY longer to download over satellite.`;
export const CHAT_FILE_UPLOAD_CONFIRMATION_MESSAGE = `Are you sure you want to upload it?`;
export const CHAT_FILE_UPLOAD_DRAGDROP_CAPTION = `Enter a caption to send with your file`;
export const CHAT_FILE_UPLOAD_ACTION = `Send Message`;
export const CHAT_FILE_UPLOAD_CANCEL = `Cancel`;
export const CHAT_FILE_UPLOAD_SIZE_OVERRIDE=`Override Size Limit`;
export const CHAT_FILE_UPLOAD_CAPTION_PLACEHOLDER = `Enter a caption to send with your file`;
export const CHAT_FILE_UPLOAD_CAPTION_LABEL = `Caption`;

export const CHAT_MSG_TEXTAREA_PLACEHOLDER = `Send a message`;
export const CHAT_MSG_TEXTAREA_LOADING_PLACEHOLDER = `Loading conversation...`;